/**
 * Rate de desplazamiento alineado con el backend (GTK-526):
 * `displacementRate` en API/DB = ficticios a desplazar por cada usuario real.
 * La etiqueta visible es el ratio real:ficticio (valor N → "1:N").
 */
export const DISPLACEMENT_RATE_MAX = 6;

const buildDisplacementRateLabel = (ficticiosPerReal: number): string =>
  `1:${ficticiosPerReal}`;

const buildDisplacementRateHelper = (ficticiosPerReal: number): string =>
  `1 usuario real : ${ficticiosPerReal} ficticio${ficticiosPerReal === 1 ? "" : "s"}`;

export const DISPLACEMENT_RATE_OPTIONS = Array.from(
  { length: DISPLACEMENT_RATE_MAX },
  (_, index) => {
    const value = index + 1;
    return {
      label: buildDisplacementRateLabel(value),
      value,
    };
  }
);

export type DisplacementRateWireValue = (typeof DISPLACEMENT_RATE_OPTIONS)[number]["value"];

const LABEL_BY_WIRE_VALUE = new Map<number, string>(
  DISPLACEMENT_RATE_OPTIONS.map((option) => [option.value, option.label])
);

const HELPER_BY_WIRE_VALUE = Object.fromEntries(
  DISPLACEMENT_RATE_OPTIONS.map((option) => [
    option.value,
    buildDisplacementRateHelper(option.value),
  ])
) as Record<DisplacementRateWireValue, string>;

export const DISPLACEMENT_RATE_WIRE_VALUES = DISPLACEMENT_RATE_OPTIONS.map(
  (option) => option.value
);

/** Valores admitidos en el formulario (subset del backend: entero ≥ 1). */
export const isDisplacementRateWireValue = (value: number): value is DisplacementRateWireValue =>
  Number.isInteger(value) && value >= 1 && value <= DISPLACEMENT_RATE_MAX;

/** Etiqueta visual para tabla y lectura humana (ratio real:ficticio). */
export const formatDisplacementRateLabel = (wireValue: number): string => {
  const known = LABEL_BY_WIRE_VALUE.get(wireValue);
  if (known) {
    return known;
  }
  if (Number.isInteger(wireValue) && wireValue >= 1) {
    return buildDisplacementRateLabel(wireValue);
  }
  return String(wireValue);
};

/** Texto de ayuda bajo el selector del formulario. */
export const getDisplacementRateHelperText = (
  wireValue: number | undefined
): string | undefined => {
  if (wireValue == null || !Number.isInteger(wireValue) || wireValue < 1) {
    return undefined;
  }
  return (
    HELPER_BY_WIRE_VALUE[wireValue as DisplacementRateWireValue] ??
    buildDisplacementRateHelper(wireValue)
  );
};

/** Opciones del dropdown; incluye el valor actual si está fuera del rango habitual. */
export const getDisplacementRateOptions = (currentValue?: number) => {
  const options = [...DISPLACEMENT_RATE_OPTIONS];
  if (
    currentValue != null &&
    Number.isInteger(currentValue) &&
    currentValue >= 1 &&
    !options.some((option) => option.value === currentValue)
  ) {
    options.push({
      label: buildDisplacementRateLabel(currentValue),
      value: currentValue,
    });
    options.sort((a, b) => a.value - b.value);
  }
  return options;
};

/** Valor del formulario → payload API (entero = ficticios por real). */
export const toApiDisplacementRate = (formValue: number | undefined): number =>
  Number(formValue ?? 1);

/** Valor API → formulario para edición. */
export const fromApiDisplacementRate = (apiValue: number): number => apiValue;
